import { useState } from "react";
import { CHARACTERS, NEEDS } from "@/data/characters";

const field = "mt-2 w-full bg-transparent border border-[#303B2D] rounded-sm px-3 py-2.5 text-[15px] text-[#E8DDC7] focus:outline-none focus:border-[#B5A642]/70 transition-colors";
const label = "text-[10px] uppercase tracking-[0.25em] text-[#B5A642]/80";

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-pressed={on}
    className={`px-3.5 py-2 text-[13px] rounded-sm border transition-colors duration-300 ${on ? "border-[#B5A642] bg-[#B5A642]/10 text-[#E8DDC7]" : "border-[#303B2D] text-[#D3CEC4]/70 hover:border-[#B5A642]/50"}`}>
    {children}
  </button>
);

export const DogForm = ({ t, lang, initial, onSave, onCancel, saving, error }) => {
  const [d, setD] = useState({ name: "", characters: [], age_years: "", weight_lb: "", need: "", email_notes: true, birthday: "", birthday_letter: true, ...initial });
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const toggle = (id) => set("characters", d.characters.includes(id) ? d.characters.filter((c) => c !== id) : [...d.characters, id]);
  const ready = d.name.trim() && d.characters.length && Number(d.weight_lb) > 0;
  const submit = (e) => {
    e.preventDefault();
    if (!ready) return;
    onSave({ name: d.name.trim(), characters: d.characters, age_years: d.age_years === "" ? null : Number(d.age_years),
      weight_lb: Number(d.weight_lb), need: d.need || null, email_notes: d.email_notes, birthday: d.birthday || null, birthday_letter: d.birthday_letter, lang });
  };
  return (
    <form onSubmit={submit} className="border border-[#B5A642]/30 rounded-sm p-6 space-y-6" data-testid="dog-form">
      <div className="grid sm:grid-cols-3 gap-4">
        <label className="block sm:col-span-1"><span className={label}>{t("Name", "Nom")}</span>
          <input data-testid="dog-form-name" className={field} maxLength={40} value={d.name} onChange={(e) => set("name", e.target.value)} /></label>
        <label className="block"><span className={label}>{t("Weight (lb)", "Poids (lb)")}</span>
          <input data-testid="dog-form-weight" type="number" min="1" max="250" className={field} value={d.weight_lb} onChange={(e) => set("weight_lb", e.target.value)} /></label>
        <label className="block"><span className={label}>{t("Age (years)", "Âge (ans)")}</span>
          <input data-testid="dog-form-age" type="number" min="0" max="25" step="0.5" className={field} value={d.age_years ?? ""} onChange={(e) => set("age_years", e.target.value)} /></label>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <label className="block"><span className={label}>{t("Birthday (optional)", "Anniversaire (facultatif)")}</span>
          <input data-testid="dog-form-birthday" type="date" max={new Date().toISOString().slice(0, 10)} className={`${field} [color-scheme:dark]`} value={d.birthday || ""} onChange={(e) => set("birthday", e.target.value)} /></label>
        {d.birthday && (
          <label className="flex items-start gap-3 sm:col-span-2 sm:pt-7 text-[14px] text-[#D3CEC4]/75 cursor-pointer">
            <input type="checkbox" data-testid="dog-form-birthday-letter" checked={d.birthday_letter} onChange={(e) => set("birthday_letter", e.target.checked)} className="mt-1 accent-[#B5A642]" />
            {t("Send a birthday letter on the day, with a $5 treat code.", "Envoyer une lettre d'anniversaire le jour venu, avec un code gâterie de 5 $.")}
          </label>
        )}
      </div>
      <div>
        <p className={label}>{t("Who is this dog? Pick all that fit.", "Qui est ce chien? Choisissez tout ce qui s'applique.")}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {CHARACTERS.map((c) => <Chip key={c.id} on={d.characters.includes(c.id)} onClick={() => toggle(c.id)} testid={`dog-form-character-${c.id}`}>{t(...c.title)}</Chip>)}
        </div>
      </div>
      <div>
        <p className={label}>{t("Anything in particular? (optional)", "Un besoin particulier? (facultatif)")}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {NEEDS.map((n) => <Chip key={n.id} on={d.need === n.id} onClick={() => set("need", d.need === n.id ? "" : n.id)} testid={`dog-form-need-${n.id}`}>{t(...n.label)}</Chip>)}
        </div>
      </div>
      <label className="flex items-start gap-3 text-[14px] text-[#D3CEC4]/75 cursor-pointer">
        <input type="checkbox" data-testid="dog-form-email-notes" checked={d.email_notes} onChange={(e) => set("email_notes", e.target.checked)} className="mt-1 accent-[#B5A642]" />
        {t("Email me a quiet note when this dog's jar is about a week from empty.", "M'envoyer un petit mot quand le pot de ce chien sera vide d'ici une semaine environ.")}
      </label>
      {error && <p data-testid="dog-form-error" className="text-[13px] text-[#C9876B]">{error}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={!ready || saving} data-testid="dog-form-save"
          className="px-6 py-3 bg-[#4B3226] text-[#E8DDC7] text-[11px] uppercase tracking-[0.2em] rounded-sm hover:brightness-125 disabled:opacity-40 transition-[filter,opacity] duration-300">
          {saving ? t("Writing it down…", "On l'inscrit…") : t("Save to the ledger", "Inscrire au registre")}
        </button>
        <button type="button" onClick={onCancel} data-testid="dog-form-cancel" className="px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4]/60 hover:text-[#E8DDC7] transition-colors">
          {t("Never mind", "Annuler")}
        </button>
      </div>
    </form>
  );
};
